import { useCallback, useEffect, useMemo, useState } from 'react'
import { useMutation } from '@apollo/client'
import { Button } from '@/atoms/Button'
import { EmbeddedStripeCheckoutSession } from '@/molecules/EmbeddedStripeCheckoutSession'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { getStringFromSessionStorage } from '@/utils/session-storage'
import { useTranslate } from '@/utils/translate/translate-client'
import { Page } from '../../components/Page'
import {
  TorchApplicationStatus,
  TorchApplicationTypes,
  useTorchApplication,
} from '../../components/TorchApplicationContext'
import { CREATE_TORCH_APPLICATION_STRIPE_CHECKOUT } from '../../queries'
import { useUserGuildProjectQuery } from '../AssociatedProject/hooks/useUserGuildProjectQuery'
import { SubmissionSuccess } from './SubmissionSuccess'

interface GetTorchPaymentPriorPageProps {
  isEpisode: boolean
  hasProjects: boolean
}

const getTorchPaymentPriorPage = ({ isEpisode, hasProjects }: GetTorchPaymentPriorPageProps) => {
  if (isEpisode && hasProjects) return 5

  return 4
}

export const TorchApplicationPayment = () => {
  const { t } = useTranslate('common')
  const { query } = useSafeRouter()
  const { setPage, data, id, type, applicationProjectId, updateStatus } = useTorchApplication()
  const isEpisode = useMemo(() => type === TorchApplicationTypes.CompletedEpisode, [type])
  const [resubmissionSuccess, setResubmissionSuccess] = useState(false)
  const [episodeProjectPassedShowSuccess, setEpisodeProjectPassedShowSuccess] = useState(false)

  const isResubmission = useMemo(() => data?.isResubmission, [data])

  const [createSession, { data: checkoutSession, loading }] = useMutation(CREATE_TORCH_APPLICATION_STRIPE_CHECKOUT, {
    errorPolicy: 'all',
  })
  const { data: projectData } = useUserGuildProjectQuery()
  const hasPaid = !!data?.paymentDate || query.success

  const handleProjectPassedOrResubmission = useCallback(() => {
    updateStatus(TorchApplicationStatus.PendingReview)
    if (isResubmission) {
      setResubmissionSuccess(true)
    } else {
      setEpisodeProjectPassedShowSuccess(true)
    }
  }, [updateStatus, isResubmission])

  useEffect(() => {
    const promoCode = getStringFromSessionStorage('torchSubmissionPromoCode')
    if (!checkoutSession && id && !hasPaid) {
      createSession({ variables: { input: { id, promoCode } } })
    }
  }, [checkoutSession, createSession, id, hasPaid])

  const isEpisodeProjectPassed = useMemo(() => {
    return (
      projectData?.guildUserProjects?.find((project) => project?.id === applicationProjectId && project?.isPassed) &&
      isEpisode
    )
  }, [applicationProjectId, isEpisode, projectData?.guildUserProjects])

  if (isResubmission) {
    return (
      <Page
        disabled={!hasPaid}
        onBack={() =>
          setPage(getTorchPaymentPriorPage({ isEpisode, hasProjects: !!projectData?.guildUserProjects?.length }))
        }
      >
        {resubmissionSuccess ? (
          <SubmissionSuccess />
        ) : (
          <Button variant="oxide" onClick={handleProjectPassedOrResubmission} className="w-72">
            {t('submitApplication', 'Submit Application')}
          </Button>
        )}
      </Page>
    )
  }

  return (
    <Page
      disabled={!hasPaid}
      onBack={() =>
        setPage(getTorchPaymentPriorPage({ isEpisode, hasProjects: !!projectData?.guildUserProjects?.length }))
      }
    >
      {isEpisodeProjectPassed && !episodeProjectPassedShowSuccess && (
        <Button variant="oxide" onClick={handleProjectPassedOrResubmission} className="w-72">
          {t('submitApplication', 'Submit Application')}
        </Button>
      )}
      {isEpisodeProjectPassed && episodeProjectPassedShowSuccess && <SubmissionSuccess />}
      {!isEpisodeProjectPassed && hasPaid && <SubmissionSuccess />}
      {!isEpisodeProjectPassed && !hasPaid && (
        <div className="min-h-560 w-full max-w-[450px] lg:max-w-[1000px]">
          <EmbeddedStripeCheckoutSession
            clientSecret={checkoutSession?.createTorchApplicationStripeCheckout?.clientSecret}
            loading={loading}
          />
        </div>
      )}
    </Page>
  )
}
